import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import * as PIXI from 'pixi.js';
import { claimVec2 } from "../../../shared/box2dUtils";
import { AimForDirection, AimOrbit, Antibody, Buffed, ChargePopup, ColliderShape, ConsumableType, Consumer, Cooldown, EffectRadius, ElectricCharge, ElectricConductor, ElementInfo, Health, HealthPopup, Infected, InfectionGrowth, JustCombined, OnConsume, DragDropProtein, OnScreen, Physics, Placeable, PreviewOnly, RangeType, Renderable, ShowStat, Spawner, SpawnCounter, Submerged, Targeting, Transform, Virus, VirusLoadIncrease, VisibleRange } from "../../../shared/components";
import { BehaviorsConstants, Multiplayer, OverlayConstants, OverlayText, PhysicsConstants, Resolution, ShopConstants } from "../../../shared/constants";
import { Debug } from "../../../shared/debug";
import { ElementDisplayInfo } from '../../../shared/elementDisplayInfo';
import { getEntityById } from '../../../shared/entityFactory';
import { GameElement } from '../../../shared/gameElementTypes';
import { cellLocations } from '../../../shared/generated/assetLocations';
import { HighlightType } from "../../../shared/highlightType";
import { Colors } from "../palettes/colorPalette";
import { addComponent, brighten, capitalizeFirstLetter, getOrAddComponent, getSystem, metersToPixels, pixelsToMeters, rotateAround, screenToWorldPosition, worldToScreenPosition, ExecutesEvenIfPaused } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { RenderEngine } from '../rendering/renderEngine';
import { drawCooldownBar, drawDottedLine, drawProgressBar } from '../rendering/ShapePrimativeFactory';
import { PlacementSystem } from "./placementSystem";
import { getEntityPlayer } from "../../../shared/composition";
import { lerp, lerp2D } from "../../../shared/interpolation";


const ConsumeRangeColor = 0xffff00;

export class OverlayInfoSystem extends System implements ExecutesEvenIfPaused {
	pixiApp: PIXI.Application;
	game: GameClient;
	renderer: RenderEngine;
	graphicsUnderlay!: PIXI.Graphics; //TODO: Quick and dirty effects layer, this is pretty inefficient
	graphicsOverlay!: PIXI.Graphics; //TODO: Quick and dirty effects layer, this is pretty inefficient
	hpSpritePool!: CounterSpritePool;
	chargeSpritePool!: CounterSpritePool;
	elementSpritePool = new Map<number, CounterSpritePool>();
	textSpritePool: TextSpritePool;
	lastFrameHovered: Entity | null = null;
	enemyPaths = new Array<PIXI.Graphics>();
	placementSystem!: PlacementSystem;

	private flashingLineProgress = 0;
	private fpsDisplay: PIXI.BitmapText;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.renderer = this.game.renderEngine;
		this.pixiApp = this.renderer.pixiApp;
		this.textSpritePool = new TextSpritePool(this.renderer);
		this.fpsDisplay = new PIXI.BitmapText('', { font: { name: 'Arial', size: 24 }, tint: 0xffffff });
		this.pixiApp.stage.addChild(this.fpsDisplay);
		this.fpsDisplay.position.set(30, 90);
	}

	async init() {
		//TODO: Factor out common rendering dependendencies to a renderer class or renderService
		this.graphicsOverlay = this.renderer.graphicsOverlay;
		this.graphicsUnderlay = this.renderer.graphicsUnderlay;
		this.hpSpritePool = new CounterSpritePool(
			this.renderer.spriteFactory.getTexture('heart'),
			42, 50, OverlayText.OverlayTextStyle,
			new PIXI.Point(0.5, 0.5), new PIXI.Point(0.5, 0.5)
		);
		this.chargeSpritePool = new CounterSpritePool(
			this.renderer.spriteFactory.getTexture('spiky_speech_bubble'),
			42, 50, OverlayText.OverlayElectricityTextStyle,
			new PIXI.Point(-0.1, -0.1), new PIXI.Point(-1.1, -0.5),
			false, Colors.Electricity
		);
	}

	execute(delta: number, time: number) {
		this.executeEvenIfPaused(delta, time);
	}

	executeEvenIfPaused(delta: number, time: number) {
		if (Debug.ShowFPS) {
			let fps = 1000 / this.game.frameDeltaTime;
			if (fps > 58) fps = 60;
			this.fpsDisplay.text = `${fps.toFixed(0)} fps`;
		}

		let hovered = this.game.hoveredEntity !== null ? getEntityById(this.game.hoveredEntity) : null;
		if (hovered && !(hovered as any).alive) hovered = null; //removed ecsy entities throw if components are added
		if (!this.placementSystem) this.placementSystem = getSystem(this.game.world, PlacementSystem);
		const dragged = this.placementSystem.getCurrentlyDraggedEntity();

		if (hovered) {
			const show = getOrAddComponent(hovered, ShowStat);
			show.stats.add('range');
			show.stats.add('effectRadius');
			show.remainingMS = PhysicsConstants.StepRate;

			if (hovered.hasComponent(Antibody)) {
				const comp = this.game.getEntityCompElement(hovered);
				if (comp) {
					const renderable = hovered.getComponent(Renderable);
					const transform = hovered.getComponent(Transform);
					let { position } = transform.getRenderTransform();
					position = this.renderer.getScreenCoordinate(position);

					//Cell description
					this.game.setInspection({ element: comp.type, position, compElement: comp });

					//Triple / Combined cell progress
					if (comp.combined > 0) {
						const pipSize = 5;
						const maxPips = ShopConstants.PromotionNumber - 1;
						const space = pipSize + 15;
						const x = position.x - (space * maxPips) / 4;
						const y = position.y + (renderable.sprite.height);

						for (let i = 0; i < maxPips; ++i) {
							let color = comp.combined > i ? 0x00ff00 : 0x000000;
							color = comp.promoted ? HighlightType.Golden : color;
							this.graphicsOverlay.beginFill(color);
							this.graphicsOverlay.drawCircle(x + (i * space), y, pipSize);
							this.graphicsOverlay.endFill();
							this.graphicsOverlay.lineStyle(2, 0xffffff);
							this.graphicsOverlay.drawCircle(x + (i * space), y, pipSize + 1);
							this.graphicsOverlay.lineStyle(0);
						}
					}

					if (this.game.inSimulation && hovered.hasComponent(Cooldown)) {
						//in the shop cooldowns hold their starting values (some start partly randomized) which just looks like a confusing half-charged bar
						const cooldown = hovered.getComponent(Cooldown);
						const progress = cooldown.remaining / cooldown.cooldown;

						const w = OverlayConstants.CooldownBarWidth;
						const h = OverlayConstants.CooldownBarHeight;
						const x = position.x - w / 2;
						const y = position.y - renderable.sprite.height * 0.8;
						drawCooldownBar(this.graphicsOverlay,
							x, y, w, h,
							progress,
							OverlayConstants.CooldownBarFullColor,
							OverlayConstants.CooldownBarEmptyColor
						);
					}

					if (hovered.hasComponent(Consumer)) {
						//Consumer range
						const consumer = hovered.getComponent(Consumer);
						//We only use range for capsid proteins right now.
						if (consumer.types.includes(ConsumableType.CapsidProtein)) {
							this.renderRange(hovered, consumer.range, ConsumeRangeColor);
						}

						//Consumption progress
						if (hovered.hasComponent(OnConsume)) {
							const onConsume = hovered.getComponent(OnConsume);
							for (let i = 0; i < onConsume.requiredAmount.length; ++i) {
								const required = onConsume.requiredAmount[i].amount;
								const consumedAmount = consumer.consumedAmounts[onConsume.requiredAmount[i].type];
								const progress = Math.min(1, consumedAmount / required);

								const w = OverlayConstants.CooldownBarWidth;
								const h = OverlayConstants.ConsumeProgressBarHeight;
								const x = position.x + w / 2 + renderable.sprite.width / 4;
								const y = position.y + (i * (h * 1.4)) - ((onConsume.requiredAmount.length * h) / 2);
								drawProgressBar(this.graphicsOverlay,
									x, y, w, h,
									progress,
									OverlayConstants.ConsumeProgressBarColor,
									OverlayConstants.CooldownBarEmptyColor
								);
							}
						}
					}

					if (hovered.hasComponent(DragDropProtein)) {
						this.renderDragDropperUI(hovered, transform);
					}
				}
			}

			if (hovered.hasComponent(Infected)) {
				const transform = hovered.getComponent(Transform);
				let { position } = transform.getRenderTransform();
				const spawner = hovered.getComponent(Spawner);
				const type = this.getSpawnedType(spawner);
				this.game.setInspection({ element: type, position });
			}
			if (hovered.hasComponent(Virus)) {
				const transform = hovered.getComponent(Transform);
				let { position } = transform.getRenderTransform();
				const info = hovered.getComponent(ElementInfo);
				this.game.setInspection({ element: info.type, position });
			}

			if (hovered.hasComponent(ElectricConductor)) {
				for (const electric of this.queries.conductors.results) {
					const conductor = electric.getComponent(ElectricConductor);
					if (conductor.accessCharge) {
						const transform = electric.getComponent(Transform);
						const pos = this.renderer.getScreenCoordinate(worldToScreenPosition(transform.position));
						this.renderFilledCircle(pos, BehaviorsConstants.ConductionRange, Colors.BackgroundRaised);
					}
				}
			}
		}
		else if (this.lastFrameHovered) {
			this.game.setInspection(null);
		}
		this.lastFrameHovered = hovered;

		//Some duplicate stuff in dragged. Bascially range and drag drop UI
		if (dragged) {
			const show = getOrAddComponent(dragged, ShowStat);
			show.stats.add('range');
			show.stats.add('effectRadius');
			show.remainingMS = PhysicsConstants.StepRate;

			if (dragged.hasComponent(DragDropProtein)) {
				this.renderDragDropperUI(dragged);
			}

			if (dragged.hasComponent(Consumer)) {
				//Consumer range
				const consumer = dragged.getComponent(Consumer);
				//We only use range for capsid proteins right now.
				if (consumer.types.includes(ConsumableType.CapsidProtein)) {
					this.renderRange(dragged, consumer.range, ConsumeRangeColor);
				}
			}
		}

		const showBuff = (entity: Entity) => {
			const buffSignal = entity.getComponent(Buffed);
			if (buffSignal.lastBuffed !== 'none') {
				const stat = buffSignal.lastBuffed;
				const value = buffSignal.lastBuffedValue;

				//Show persistent effects
				const show = getOrAddComponent(entity, ShowStat);
				show.stats.add(buffSignal.lastBuffed);
				show.remainingMS = 1000;

				//Show floating text
				if (entity.hasComponent(Transform)) { //might be destroyed if round changes
					const transform = entity.getComponent(Transform);
					const statName = capitalizeFirstLetter(stat);
					this.textSpritePool.floatText(`${Math.sign(value) ? '+' : '-'} ${value} ${statName}`, transform, time);
				}
			}
		};

		this.queries.buffed.added?.forEach(entity => {
			showBuff(entity);
		});
		this.queries.buffed.changed?.forEach(entity => {
			showBuff(entity);
		});

		const showStatRemove = new Array<Entity>();
		for (const entity of this.queries.buffsToShow.results) {
			const show = entity.getComponent(ShowStat);
			for (const stat of show.stats) {
				if (stat === 'range' && entity.hasComponent(VisibleRange)) {
					const range = entity.getComponent(VisibleRange);
					if (range.visualize) {
						this.renderRange(entity, range.range, 0xffffff, range.type);
					}
				}
				else if (stat === 'effectRadius' && entity.hasComponent(EffectRadius)) {
					const effect = entity.getComponent(EffectRadius);
					this.renderRange(entity, effect.radius, 0xff0000);
				}
			}
			show.remainingMS -= delta;
			if (show.remainingMS <= 0) {
				showStatRemove.push(entity);
			}
		}
		showStatRemove.forEach(entity => entity.removeComponent(ShowStat));

		if (Debug.ShowTargetLine || Debug.ShowTargetingRange) {
			if (hovered) {
				const transform = hovered.getComponent(Transform);
				const { position } = transform.getRenderTransform();
				this.renderDebugTargeting(hovered, position)
			}
		}

		if (Debug.ShowHitboxes) {
			this.queries.onscreen.results.forEach(entity => {
				const transform = entity.getComponent(Transform);
				const { position, radius } = transform.getRenderTransform();
				this.renderDebugHitboxes(entity, transform, position, radius);
			});
		}

		for (const entity of [...this.queries.infectedNeedingCounter.results]) {
			const type = this.getSpawnedType(entity.getComponent(Spawner));
			const sprite = this.getElementPool(type).borrow();
			this.renderer.inSceneUI.addChild(sprite);
			entity.addComponent(SpawnCounter, { sprite, type });
		}

		for (const entity of [...this.queries.spawnCounters.results]) {
			const counter = entity.getMutableComponent(SpawnCounter)!;
			const sprite = counter.sprite!;
			if (!entity.hasComponent(Infected) || !entity.hasComponent(Spawner) || !entity.hasComponent(OnScreen)) {
				this.getElementPool(counter.type).return(sprite);
				this.renderer.inSceneUI.removeChild(sprite);
				entity.removeComponent(SpawnCounter); //A destroyed cell was only being kept for this, removing it frees the entity
				continue;
			}
			const spawner = entity.getComponent(Spawner);
			if (counter.amount !== spawner.amount) {
				setCounterSpriteText(sprite, spawner.amount);
				counter.amount = spawner.amount;
			}
			const pos = worldToScreenPosition(entity.getComponent(Transform).position);
			sprite.position.set(pos.x, pos.y - sprite.height);
		}

		for (const player of this.game.players) {
			if (player && (this.game.getCurrentState() === "shop" || this.game.getCurrentState() === "recap")) {
				//Draw a circle around the placement area
				let spawnCenter = new vec2(player.spawnPoint);
				let edge = spawnCenter.copy().add(new vec2([ShopConstants.PlacementRadius + 3, 0]));
				spawnCenter = this.renderer.getScreenCoordinate(worldToScreenPosition(spawnCenter, spawnCenter));
				edge = this.renderer.getScreenCoordinate(worldToScreenPosition(edge, edge));
				const radius = vec2.distance(spawnCenter, edge);

				this.graphicsUnderlay.lineStyle(1, 0x777777, 0.5);
				this.graphicsUnderlay.drawCircle(spawnCenter.x, spawnCenter.y, radius);
				this.graphicsUnderlay.lineStyle(0);

				//Draw dotted lines per wave that is attacking the player
				let bloodCenter = new vec2(player.bloodCellCenterPoint);
				bloodCenter = this.renderer.getScreenCoordinate(worldToScreenPosition(bloodCenter, bloodCenter));

				let from = new vec2();
				//Find average positions of each wave of spawners
				const waveSets = this.game.getVirusWaves(player);
				for (let i = 0; i < waveSets.length; ++i) {
					const wave = waveSets[i];
					from.xy = wave.center.xy;
					from = this.renderer.getScreenCoordinate(worldToScreenPosition(from, from));
					const baseColor = Colors.Virus;
					let color = baseColor;
					if (this.game.latestVirusGrowth) {
						for (const growth of this.game.latestVirusGrowth) {
							if (growth.wave === i) {
								const progress = Math.abs(this.flashingLineProgress - 0.5) * 2;
								color = brighten(baseColor, progress * 0.7);
								this.flashingLineProgress += delta / 1000;
								this.flashingLineProgress = this.flashingLineProgress % 1;
							}
							drawDottedLine(this.graphicsUnderlay, from, bloodCenter, 8, color, 0.5);
						}
					}
				}
			}
		}

		//Display orbit preview
		const hoveredOrDragged = hovered || dragged;
		if (hoveredOrDragged) {
			const orbit = hoveredOrDragged.getComponent(AimOrbit);
			if (orbit) {
				let distance = orbit.distance;
				if (orbit.distance === undefined) {
					if (orbit.orbitOwner) {
						const player = getEntityPlayer(hoveredOrDragged, this.game);
						if (!player) return;
						orbit.target = new vec2(player.bloodCellCenterPoint);
					}
					const transform = hoveredOrDragged.getComponent(Transform);
					distance = vec2.distance(orbit.target, transform.position);
				}

				if (distance > 0) {
					const center = this.renderer.getScreenCoordinate(worldToScreenPosition(orbit.target));
					this.renderOrbitPath(time, center, distance, 0xffffff);
				}
			}

			if (hoveredOrDragged.hasComponent(AimForDirection) && hoveredOrDragged.hasComponent(Transform)) {
				const aimForDirection = hoveredOrDragged.getComponent(AimForDirection);
				const transform = hoveredOrDragged.getComponent(Transform);
				const direction = aimForDirection.fixedDirection ? aimForDirection.fixedDirection : transform.forward;
				const screenDirection = direction.copy();
				screenDirection.y = -screenDirection.y;

				const arbitraryLength = Resolution.Height * 0.8;
				const from = this.renderer.getScreenCoordinate(worldToScreenPosition(transform.position));
				const to = from.copy().add(screenDirection.scale(arbitraryLength));
				this.renderLinePath(time, from, to);
			}
		}

		//Display damage text
		const healthRelevent = this.queries.health.changed || [];
		if (hovered && hovered.hasComponent(Health)) {
			healthRelevent.push(hovered);
		}
		for (const entity of healthRelevent) {
			const health = entity.getComponent(Health);
			if (health.hp > 0) {
				let popup = entity.getComponent(HealthPopup)
				if (!popup) {
					const hpSprite = this.hpSpritePool.borrow();
					this.renderer.inSceneUI.addChild(hpSprite);
					addComponent(entity, HealthPopup, { lastTimeChanged: time, sprite: hpSprite });
					popup = entity.getComponent(HealthPopup);

				}
				else {
					popup.lastTimeChanged = time;
				}

				if (hovered === entity) {
					popup.duration = 0.1; //one frame, idk
				}

				const hpLabel = popup.sprite.getChildAt(1) as PIXI.BitmapText;
				if (health.hp.toString() !== hpLabel.text) {
					hpLabel.text = `${health.hp}`;
				}
			}
		};

		const healthPopupRemove = new Array<Entity>();
		this.queries.healthPopups.results.forEach(entity => {
			const transform = entity.getMutableComponent(Transform);
			const popup = entity.getComponent(HealthPopup);

			const fade = 1 - ((time - popup.lastTimeChanged) / (popup.duration * 1000));
			const position = worldToScreenPosition(transform.position);
			popup.sprite.position.set(position.x, position.y);

			if (fade < 0) {
				healthPopupRemove.push(entity);
			}
			else {
				popup.sprite.alpha = fade;
			}
		});
		healthPopupRemove.forEach(entity => entity.removeComponent(HealthPopup));

		this.queries.healthPopups.removed?.forEach(entity => {
			const popup = entity.getComponent(HealthPopup) || entity.getRemovedComponent(HealthPopup);
			popup.sprite.alpha = 1;
			this.renderer.inSceneUI.removeChild(popup.sprite);
			this.hpSpritePool.return(popup.sprite);
		});

		//Display combine text
		this.queries.newImmuneCells.added?.forEach(entity => {
			const compEl = this.game.getEntityCompElement(entity);
			if (!compEl) return;

			if (compEl.combined < 1) return;

			const isTriple = compEl.combined === 2;
			const color = isTriple ? Colors.UICarb : Colors.UIIncomingTabText;
			const hexcolor = PIXI.utils.string2hex(color);

			const transform = entity.getComponent(Transform);
			const position = transform.position;
			const text = `${compEl.combined + 1}/3`;

			const fontSize = 100;
			const fontSizeMeters = pixelsToMeters(fontSize);
			position.y += transform.size.y / 2 + fontSizeMeters / 2;
			position.x -= (fontSizeMeters * text.length / 2) / 2;
			this.textSpritePool.floatText(text, { position }, time, 1.5, 1, {
				align: 'center', fontStyle: 'normal', fontSize: `${fontSize}pt`, fill: hexcolor, stroke: color, strokeThickness: 2
			});
		});

		//Display charge text
		const chargeRelevent = this.queries.electricity.changed || [];
		if (hovered && hovered.hasComponent(ElectricCharge)) {
			chargeRelevent.push(hovered);
		}
		if (dragged && dragged.hasComponent(ElectricCharge)) {
			chargeRelevent.push(dragged);
		}
		for (const entity of chargeRelevent) {
			const electricity = entity.getComponent(ElectricCharge);
			if (electricity.charge > 0) {
				let popup = entity.getComponent(ChargePopup)
				if (!popup) {
					const hpSprite = this.chargeSpritePool.borrow();
					this.renderer.inSceneUI.addChild(hpSprite);
					addComponent(entity, ChargePopup, { lastTimeChanged: time, sprite: hpSprite });
					popup = entity.getComponent(ChargePopup);

				}
				else {
					popup.lastTimeChanged = time;
				}

				if (hovered === entity) {
					popup.duration = 0.1; //one frame, idk
				}

				const label = popup.sprite.getChildAt(1) as PIXI.BitmapText;
				if (electricity.charge.toString() !== label.text) {
					label.text = `${electricity.charge}`;
				}
			}
		};

		const chargePopupRemove = new Array<Entity>();
		this.queries.chargePopups.results.forEach(entity => {
			const transform = entity.getMutableComponent(Transform);
			const popup = entity.getComponent(ChargePopup);

			const fade = 1 - ((time - popup.lastTimeChanged) / (popup.duration * 1000));
			const position = worldToScreenPosition(transform.position);
			popup.sprite.position.set(position.x, position.y);

			if (fade < 0) {
				chargePopupRemove.push(entity);
			}
			else {
				popup.sprite.alpha = fade;
			}
		});
		chargePopupRemove.forEach(entity => entity.removeComponent(ChargePopup));

		this.queries.chargePopups.removed?.forEach(entity => {
			const popup = entity.getComponent(ChargePopup) || entity.getRemovedComponent(ChargePopup);
			popup.sprite.alpha = 1;
			this.renderer.inSceneUI.removeChild(popup.sprite);
			this.chargeSpritePool.return(popup.sprite);
		});

		this.textSpritePool.updateTextSpritePool(time);

		//Infection growth
		for (const entity of this.queries.infectionGrowth.results) {
			const transform = entity.getComponent(Transform); //not in query so we can use changed event..
			if (!transform) return;
			this.textSpritePool.floatText(`+1`, transform, time, 1.0);
		};
	}

	cachedPositionVec = new vec2();
	renderDragDropperUI(entity: Entity, transform = entity.getComponent(Transform)) {
		//Print in text the number of remaining drops
		const onDrag = entity.getComponent(DragDropProtein);
		const drops = onDrag.triggers;
		const text = `x${drops}`;
		this.cachedPositionVec.x = transform.size.x / 2 + 0.3;
		this.cachedPositionVec.y = transform.size.y / 4;
		this.cachedPositionVec.add(transform.position)
		this.textSpritePool.showTextThisFrame(text, { position: this.cachedPositionVec });
	}

	getElementPool(el: GameElement): CounterSpritePool {
		const key = el;
		let pool = this.elementSpritePool.get(key);
		if (!pool) {
			const atlas = this.renderer.spriteFactory.colorSprites;

			const info = ElementDisplayInfo.get(el);
			if (!info) throw new Error("UI Display info not initialized when needed for spawner overlay");
			const location = (cellLocations as any)[info.asset]
			if (!location) { throw new Error(`Tried to look up non-existant overlay: ${info.displayName}, ${info.asset}`); }

			const rect = new PIXI.Rectangle(location.x, location.y, location.width, location.height);
			const tex = new PIXI.Texture(atlas, rect)
			pool = new CounterSpritePool(tex, 30, 1, OverlayText.OverlayTextStyle, new PIXI.Point(1, 1), new PIXI.Point(0, 1), true);
			this.elementSpritePool.set(key, pool);
		}
		return pool;
	}

	renderRange(entity: Entity, range: number, color: number = 0xffffff, type = RangeType.Circle,) {
		const transform = entity.getComponent(Transform);
		let { position } = transform.getRenderTransform();
		position = this.renderer.getScreenCoordinate(position);

		if (type === RangeType.Circle) {
			this.renderCircle(position, range, color);
		}

		else if (type === RangeType.Line) {
			let end = transform.forward.copy().scale(range).add(transform.position);
			end = this.renderer.getScreenCoordinate(worldToScreenPosition(end, end));
			this.graphicsOverlay.lineStyle(2, color, 1.0);
			this.graphicsOverlay.moveTo(position.x, position.y);
			this.graphicsOverlay.lineTo(end.x, end.y);
			this.graphicsOverlay.lineStyle(0);
		}
	}

	renderCircle(center: vec2, radius: number, color: number = 0xffffff) {
		const pxRange = metersToPixels(radius);
		this.graphicsUnderlay.lineStyle(2, color);
		this.graphicsUnderlay.drawCircle(center.x, center.y, pxRange * this.renderer.sceneRoot.scale.x);
		this.graphicsUnderlay.lineStyle(0);
	}

	renderOrbitPath(time: number, center: vec2, radius: number, color = 0xffffff, reducedColor = 0x999999) {
		//Draw a dotted circle
		let segments = radius * 5;
		const step = Math.PI * 2 / segments;
		const pxRange = metersToPixels(radius);
		const emphasizedSegment = Math.floor(segments - (time / 20) % segments);
		//render leaving gap between segments
		for (let i = 0; i < segments; i++) {
			const renderColor = i === emphasizedSegment ? color : reducedColor;
			const segmentWidth = i === emphasizedSegment ? 12 : 4;
			this.graphicsUnderlay.lineStyle(segmentWidth, renderColor, 1);
			const angle = step * i;
			const x = center.x + Math.cos(angle) * pxRange * this.renderer.sceneRoot.scale.x;
			const y = center.y + Math.sin(angle) * pxRange * this.renderer.sceneRoot.scale.x;
			if (i % 2 === 0) this.graphicsUnderlay.moveTo(x, y);
			else this.graphicsUnderlay.lineTo(x, y);
		}
		this.graphicsUnderlay.lineStyle(0);
	}

	//Later
	renderLinePath(time: number, from: vec2, to: vec2, color = 0xffffff, reducedColor = 0x999999) {
		//Render a dotted line path between the points
		const distance = vec2.distance(from, to);
		const segments = distance / 15;

		const emphasizedSegment = Math.floor((time / 15) % segments);
		for (let i = 0; i < segments; i++) {
			const renderColor = i === emphasizedSegment ? color : reducedColor;
			const segmentWidth = i === emphasizedSegment ? 12 : 4;
			this.graphicsUnderlay.lineStyle(segmentWidth, renderColor, 1);
			const pos = vec2.mix(from, to, i / segments);
			//lerp2D(from, to, i * step, pos);
			if (i % 2 === 0) this.graphicsUnderlay.moveTo(pos.x, pos.y);
			else this.graphicsUnderlay.lineTo(pos.x, pos.y);
		}
		this.graphicsUnderlay.lineStyle(0);
	}

	renderFilledCircle(center: vec2, radius: number, color: number = 0xffffff, alpha = 1) {
		const pxRange = metersToPixels(radius);
		this.graphicsUnderlay.beginFill(color, alpha);
		this.graphicsUnderlay.drawCircle(center.x, center.y, pxRange * this.renderer.sceneRoot.scale.x);
		this.graphicsUnderlay.endFill();
	}

	renderDebugHitboxes(entity: Entity, transformComponent: Transform, position: vec2, radius: number) {
		const physics = entity.getComponent(Physics);
		if (physics) {
			position = this.renderer.getScreenCoordinate(position);
			radius *= this.renderer.sceneRoot.scale.x;
			const { x, y } = position;
			this.graphicsOverlay.beginFill(0xffffff, .5);
			if (physics.shapeType === ColliderShape.Circle) {
				this.graphicsOverlay.drawCircle(x, y, radius);
			} else {
				const body = physics.body;
				const shape = body.GetFixtureList().GetShape();
				const castedShape = B2D.castObject(shape, B2D.b2PolygonShape);
				const vertexCount = castedShape.GetVertexCount();
				const path: Array<PIXI.Point> = [];
				for (let i = 0; i < vertexCount; i++) {
					//@ts-ignore
					const vertex: b2Vec2 = castedShape.GetVertex(i);
					const screenV = claimVec2(vertex);
					rotateAround(screenV, -transformComponent.rotation, vec2.zero, screenV);
					screenV.x = metersToPixels(screenV.x);
					screenV.y = metersToPixels(screenV.y);
					const v = position.copy().add(screenV);
					path.push(new PIXI.Point(v.x, v.y));
				}
				this.graphicsOverlay.drawPolygon(path);
			}
			this.graphicsOverlay.endFill();
		}
	}

	renderDebugTargeting(entity: Entity, position: vec2) {
		position = this.renderer.getScreenCoordinate(position);
		const targeting = entity.getComponent(Targeting);
		if (targeting) {
			const sceneOffset = this.renderer.sceneRoot.position;
			for (let i = 0; i < targeting.specs.length; ++i) {
				const spec = targeting.specs[i];
				const results = targeting.getTargets(i);
				if (spec.nearestOnly && Debug.ShowTargetLine && results.length > 0) {
					const nearest = results[0];
					const targetTransform = nearest.getComponent(Transform);
					const targetPos = targetTransform.getRenderTransform().position;
					const { x, y } = position;
					const color = 0xffffff;
					this.graphicsOverlay.lineStyle(1, color, 1.0);
					this.graphicsOverlay.moveTo(x, y);
					this.graphicsOverlay.lineTo(sceneOffset.x + targetPos.x, sceneOffset.y + targetPos.y);
					this.graphicsOverlay.lineStyle(0);
				}
				if (Debug.ShowTargetingRange) {
					const { x, y } = position;
					if (!spec.nearestOnly) {
						const radius = metersToPixels(spec.range) * this.renderer.sceneRoot.scale.x;
						const color = 0x0000ff;
						this.graphicsOverlay.beginFill(color, 0.5);
						this.graphicsOverlay.drawCircle(sceneOffset.x + x, sceneOffset.y + y, radius);
						this.graphicsOverlay.endFill();
					}
					else {
						const color = 0x00ff00;
						const radius = metersToPixels(spec.range) * this.renderer.sceneRoot.scale.x;;
						this.graphicsOverlay.beginFill(color, 0.5);
						this.graphicsOverlay.drawCircle(sceneOffset.x + x, sceneOffset.y + y, radius);
						this.graphicsOverlay.endFill();
					}
				}
			}
		}
	}

	getSpawnedType(spawner: Spawner) {
		const type = spawner.spawns[0].type;
		return type;
	}
}

class CounterSpritePool {
	pool = new Array<PIXI.Container>();
	constructor(
		private texture: PIXI.Texture,
		private spriteSize: number,
		estimatedNumberOfSprite: number,
		private style: Object,
		private anchor: PIXI.Point,
		private textAnchor: PIXI.Point,
		private offsetY = false,
		private tint: number = 0xffffff,
	) {
		//Pre-fill the pool
		for (let i = 0; i < estimatedNumberOfSprite; ++i) {
			this.return(this.createSprite());
		}
	}

	borrow() {
		if (this.pool.length === 0) {
			this.pool.push(this.createSprite());
		}
		return this.pool.pop()!;
	}

	return(sprite: PIXI.Container | null) {
		if (!sprite) return;
		this.pool.push(sprite);
	}

	private createSprite() {
		const container = new PIXI.Container();
		const sprite = new PIXI.Sprite(this.texture);
		sprite.width = this.spriteSize;
		sprite.height = this.spriteSize;
		sprite.tint = this.tint;
		//const text = new PIXI.Text(`NA`, new PIXI.TextStyle(OverlayText.OverlayTextStyle))
		const text = new PIXI.Text(`NA`, new PIXI.TextStyle(this.style))
		sprite.anchor = this.anchor;
		text.anchor = this.textAnchor;
		if (this.offsetY) {
			text.position.y = (-this.spriteSize / 2) + (text.width / 2);
		}

		//const text = new PIXI.BitmapText(`NA`, { font: '22px Arial', align: 'left' } as any);
		//text.anchor = 0.5;
		//text.tint = this.textTint;
		container.addChild(sprite)
		container.addChild(text);
		return container;
	}
}

function setCounterSpriteText(sprite: PIXI.Container, count: number) {
	const icon = sprite.getChildAt(0) as PIXI.Sprite;
	const text = sprite.getChildAt(1) as PIXI.Text;
	text.visible = count > 0;
	icon.visible = count > 0;
	if (text.text !== ` x ${count}`) {
		text.text = ` x ${count}`;
	}
}

class TextSpritePool {

	free: Array<PIXI.Text>;
	inUse: Array<{ sprite: PIXI.Text, transform: { position: vec2 }, lastPos: vec2, expires: number, floatSpeed: number, floatAmount: 0 }>;
	oneFrameUseNow: Array<{ sprite: PIXI.Text, transform: { position: vec2 }, lastPos: vec2, useScreen: boolean }>;
	oneFrameUseExpired: Array<{ sprite: PIXI.Text, transform: { position: vec2 }, lastPos: vec2 }>;

	constructor(public renderer: RenderEngine) {
		this.free = [];
		this.inUse = [];
		this.oneFrameUseNow = [];
		this.oneFrameUseExpired = [];
	}

	get(text: string, transform: { position: vec2 }, style: any) {
		const textSprite = this.free.pop() || new PIXI.Text(text, new PIXI.TextStyle(style));
		textSprite.style = style;
		textSprite.alpha = 1;
		textSprite.text = text;
		textSprite.alpha = 1;
		const worldPosition = transform.position.copy();
		const screenPosition = worldToScreenPosition(worldPosition, worldPosition);
		textSprite.position.set(...screenPosition.xy);
		this.renderer.inSceneUI.addChild(textSprite);
		return textSprite;
	}

	recycle(textSprite: PIXI.Text) {
		this.renderer.inSceneUI.removeChild(textSprite);
		this.free.push(textSprite);
	}

	floatText(text: string, transform: { position: vec2 }, time: number, duration = OverlayText.FloatingDefaultDuration, floatSpeed = 0, style = OverlayText.OverlayFloatTextStyle) {
		const textSprite = this.get(text, transform, style);
		this.inUse.push({ sprite: textSprite, transform, lastPos: transform.position.copy(), expires: time + (duration * 1000), floatSpeed, floatAmount: 0 });
	}

	showTextThisFrame(text: string, transform: { position: vec2 }, useScreen = false) {
		const textSprite = this.get(text, transform, OverlayText.OverlayTextStyle);
		this.oneFrameUseNow.push({ sprite: textSprite, transform, lastPos: transform.position.copy(), useScreen });
	}

	updateTextSpritePool(time: number) {
		for (const text of this.oneFrameUseExpired) {
			this.oneFrameUseExpired.splice(this.oneFrameUseExpired.indexOf(text), 1);
			this.recycle(text.sprite);
		}
		for (const text of this.oneFrameUseNow) {
			this.oneFrameUseNow.splice(this.oneFrameUseNow.indexOf(text), 1);
			this.oneFrameUseExpired.push(text);
		}

		for (const text of this.inUse) {
			if (text.expires < time) {
				this.renderer.inSceneUI.removeChild(text.sprite);
				this.inUse.splice(this.inUse.indexOf(text), 1);
				this.free.push(text.sprite);
			}
			else {
				//Track the given transform, stay in place after it dies
				const worldPosition = text.transform && text.transform.position ? text.transform.position.copy() : text.lastPos.copy();
				const screenPosition = worldToScreenPosition(worldPosition, worldPosition);
				text.sprite.position.set(...screenPosition.xy);
				text.lastPos = worldPosition;

				//Fade out for 0.15 seconds before the expiry
				const remaining = text.expires - time;
				const fade = Math.min(1, remaining / (OverlayText.FloatingFadeOutDuration * 1000));
				text.sprite.alpha = fade;
				text.floatAmount += text.floatSpeed
				text.sprite.position.y -= text.floatAmount;
			}
		}
	}
}

OverlayInfoSystem.queries = {
	onscreen: {
		components: [Renderable, OnScreen],
	},
	health: {
		components: [Health, Antibody, OnScreen], listen: {
			changed: true
		}
	},
	healthPopups: {
		components: [Renderable, HealthPopup], listen: {
			removed: true
		}
	},
	electricity: {
		components: [ElectricCharge], listen: {
			removed: true,
			changed: true,
		}
	},
	chargePopups: {
		components: [Renderable, ChargePopup], listen: {
			removed: true
		}
	},
	infectedNeedingCounter: { components: [Infected, Spawner, OnScreen, Not(SpawnCounter)] },
	spawnCounters: { components: [SpawnCounter] },
	buffed: {
		components: [Buffed], listen: { added: true, changed: true }
	},
	buffsToShow: {
		components: [ShowStat, Renderable]
	},
	conductors: {
		components: [ElectricConductor, Transform]
	},
	previewed: {
		components: [PreviewOnly, Antibody]
	},
	newImmuneCells: {
		components: [Antibody, Renderable, Transform, JustCombined], listen: { added: true }
	},
	infectionGrowth: {
		components: [InfectionGrowth, VirusLoadIncrease]
	},
}

